import {Component, OnInit, signal} from '@angular/core';
import {MatButton, MatIconButton} from '@angular/material/button';
import {MatIcon} from '@angular/material/icon';
import {TranslatePipe} from '@ngx-translate/core';
import {MatFormFieldModule} from '@angular/material/form-field';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {
  DynamicConfirmationDialogComponent
} from '../../components/dynamic-confirmation-dialog/dynamic-confirmation-dialog.component';
import {ActivatedRoute, Router} from '@angular/router';
import {DialogService} from '../../services/dialog.service';
import {UserHal} from '../../models/HAL/user-hal.model';
import {SnackBarService} from '../../services/snackbar.service';
import {DocumentCollectionHal, DocumentHal} from '../../models/HAL/document-hal.model';
import {MatExpansionModule} from '@angular/material/expansion';
import {LoadingSpinnerService} from '../../services/loading-spinner.service';
import {PopupSafeTabService} from '../../services/popup-safe-tab.service';
import {CaseHal} from '../../models/HAL/case-hal.model';
import {MatOption} from '@angular/material/core';
import {MatSelect} from '@angular/material/select';

import {HttpClient} from '@angular/common/http';
import {CreateSigningActionDto} from '../../models/DTO/CreateSigningAction.model';
import {CreateSigningActionHal} from '../../models/HAL/createSigningAction-hal.model';

@Component({
  selector: 'app-start-signature-process',
  imports: [
    MatButton,
    TranslatePipe,
    ReactiveFormsModule,
    MatIcon,
    MatIconButton,
    MatFormFieldModule,
    FormsModule,
    MatExpansionModule,
    MatOption,
    MatSelect
  ],
  templateUrl: './signature-process.component.html',
  styleUrl: './signature-process.scss'
})
export class SignatureProcessComponent implements OnInit {
  protected caseCustomer = signal<UserHal | undefined>(undefined);
  protected documents: DocumentHal[] = [];
  protected selectedDocuments: DocumentHal[] = [];
  protected case: CaseHal;

  constructor(readonly activatedRoute: ActivatedRoute,
              private readonly snackBarService: SnackBarService,
              private readonly router: Router,
              private readonly httpClient: HttpClient,
              private readonly dialogService: DialogService,
              private readonly loadingSpinnerService: LoadingSpinnerService,
              private readonly popupSafeTabService: PopupSafeTabService
  ) {
    this.case = new CaseHal(activatedRoute.snapshot.data['signatureProcess']);
  }

  ngOnInit(): void {
    const documentsUrl = new URL(this.case._links.getLink('getDocuments')!.href);
    documentsUrl.searchParams.set('paged', 'false');
    this.httpClient.get<DocumentCollectionHal>(documentsUrl.href).subscribe({
      next: (response: DocumentCollectionHal) => {
        for (let document of new DocumentCollectionHal(response)._embedded?.documents || []) {
          if (document.fileMetadata
            && !!document.fileMetadata.fileId
            && document.state === 'READY'
            && document.fileMetadata.mimeType === 'application/pdf'
            && document._links.hasLink('delete')) {
            this.documents.push(document);
          }
        }
      },
      error: () => {
        this.snackBarService.open('signatureProcess.snackbar.loadError', 3, 'error');
      }
    });
    const clientUserUrl = this.case._links.getLink('getClientUser')?.href;
    this.httpClient.get<UserHal>(clientUserUrl!).subscribe({
      next: (response: UserHal) => {
        this.caseCustomer.set(response);
        this.loadingSpinnerService.hide();
      },
      error: () => {
        this.snackBarService.open('signatureProcess.snackbar.loadError', 3, 'error');
        this.loadingSpinnerService.hide();
      }
    });
  }

  protected backToCase(): void {
    const dialogRef = this.dialogService.open(DynamicConfirmationDialogComponent, {
      data: {
        title: 'signatureProcess.backToCaseModal.title',
        message: 'signatureProcess.backToCaseModal.message',
        confirmButtonText: 'signatureProcess.backToCaseModal.cancel',
        cancelButtonText: 'signatureProcess.backToCaseModal.confirm'
      }
    });
    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.loadingSpinnerService.show();
        this.router.navigate([this.case._links.getLink('self')?.getRoute()]);
      }
    });
  }

  protected deselectDocument(document: DocumentHal) {
    const index = this.selectedDocuments.indexOf(document);
    if (index >= 0) {
      this.selectedDocuments = [
        ...this.selectedDocuments.slice(0, index),
        ...this.selectedDocuments.slice(index + 1)
      ];
    }
  }

  protected getFullClientName() {
    const customer = this.caseCustomer();
    const firstName = customer?.firstName ?? '';
    const lastName = customer?.lastName ?? '';
    return (firstName + ' ' + lastName).trim();
  }

  protected startSignatureProcess(): void {
    this.loadingSpinnerService.show();

    const documentList: string[] = this.selectedDocuments
      .map(doc => doc.identification);

    const createSigningActionDto = new CreateSigningActionDto({
      documentIds: documentList,
      signingUrl: ''
    });

    const signingTab = this.popupSafeTabService.open({
      messageKey: 'signatureProcess.newTab.preparing',
      hintKey: 'signatureProcess.newTab.redirectHint'
    });

    this.httpClient.post<CreateSigningActionHal>(this.case._links.getLink('sign')!.href, createSigningActionDto).subscribe({
      next: (response: CreateSigningActionHal) => {
        const urlToOpen = (response.signingUrl && response.signingUrl.length > 0) ? response.signingUrl : null;
        if (urlToOpen) {
          signingTab.redirectTo(urlToOpen, {
            titleKey: 'signatureProcess.popupBlockedModal.title',
            messageKey: 'signatureProcess.popupBlockedModal.message',
            confirmKey: 'signatureProcess.popupBlockedModal.confirm',
            cancelKey: 'signatureProcess.popupBlockedModal.cancel'
          });
          this.router.navigate([this.case._links.getLink('self')?.getRoute()]);
          this.snackBarService.open('signatureProcess.snackbar.requestSuccess', 3);
        } else {
          signingTab.close();
        }
        this.loadingSpinnerService.hide();
      },
      error: () => {
        signingTab.close();
        this.snackBarService.open('signatureProcess.snackbar.requestError', 3, 'error');
        this.loadingSpinnerService.hide();
      }
    });
  }
}
